import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import {
  CheckOutlined, DownOutlined, InfoCircleOutlined, ReloadOutlined, UpOutlined,
} from '@ant-design/icons';
import styles from './GenerateCards.module.less';

/** 场景生成页两张操作卡（批量生成 / 自动扫描）共用的小部件。 */

export const Hint = ({ title }: { title: string }) => (
  <Tooltip title={title}>
    <InfoCircleOutlined style={{ color: '#9b9da3', fontSize: 12 }} />
  </Tooltip>
);

/**
 * 流程步骤条：只表达后台异步任务（tracker）能确知的阶段。
 * idle → 全灰；running → ``activeIndex`` 进行中；completed → 全部完成；
 * failed → 不确知断在哪步，不点亮已完成，仅 ``activeIndex`` 标错。
 */
export function Stepper({
  steps, status, activeIndex,
}: { steps: string[]; status?: string; activeIndex: number }) {
  const stepClass = (i: number) => {
    if (status === 'completed') return styles.stepDone;
    if (status === 'running') return i === activeIndex ? styles.stepActive : '';
    if (status === 'failed') return i === activeIndex ? styles.stepError : '';
    return '';
  };
  return (
    <div className={styles.stepper} aria-label="流程">
      {steps.map((s, i) => (
        <span key={s} style={{ display: 'contents' }}>
          {i > 0 && <span className={styles.stepLink} />}
          <span className={`${styles.step} ${stepClass(i)}`}>
            <span className={styles.stepDot}>
              {stepClass(i) === styles.stepDone ? <CheckOutlined style={{ fontSize: 9 }} /> : i + 1}
            </span>
            {s}
          </span>
        </span>
      ))}
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = {
  idle: '空闲', running: '进行中', completed: '已完成', failed: '失败',
};

const DOT_CLASS: Record<string, string> = {
  running: styles.dotRunning, completed: styles.dotCompleted, failed: styles.dotFailed,
};

/** 底部状态栏左侧：状态点 + 文案 + 统计 + Job 链接 + 错误 + 刷新（children 渲染在末尾，如进度条）。 */
export function StatusLine({
  status, runningText, meta, jobId, error, loading, onRefresh, children,
}: {
  status: string;
  runningText?: string;
  meta: string[];
  jobId?: number;
  error?: string | null;
  loading: boolean;
  onRefresh: () => void;
  children?: React.ReactNode;
}) {
  const label = status === 'running' && runningText ? runningText : (STATUS_TEXT[status] ?? status);
  return (
    <div className={styles.status} aria-live="polite">
      <div className={styles.statusRow}>
        <span className={`${styles.statusDot} ${DOT_CLASS[status] ?? ''}`} />
        <span className={styles.statusLabel}>{label}</span>
        {jobId && (
          <Link className={styles.jobLink} to={`/scene-generation/job-tasks/${jobId}`}>
            Job #{jobId}
          </Link>
        )}
        <Tooltip title="刷新任务状态">
          <Button
            size="small"
            type="text"
            aria-label="刷新任务状态"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
          />
        </Tooltip>
      </div>
      {meta.length > 0 && <div className={styles.statusMeta}>{meta.join(' · ')}</div>}
      {status === 'failed' && error && <div className={styles.statusError}>{error}</div>}
      {children}
    </div>
  );
}

/** 逐条消息的一条：``{time, level, text}``（后端只留最近 200 条）。 */
export interface BatchMessage {
  time?: string | null;
  level?: string | null;
  text?: string | null;
}

/** 只取 ``HH:mm:ss``：消息是同一批次的连续流，日期是噪音。 */
const hhmmss = (t?: string | null) => (t ? String(t).slice(-8) : '');

/**
 * 批次逐条消息（拉取进度 / 失败原因 / 下发结果）。
 *
 * 默认只显示最近 1 条 —— 它同时也是「当前在做什么」；展开后滚动看全量。
 * 消息只存 Redis（最近 200 条），刷新页面仍在，过期即散。
 */
export function MessageLog({ messages }: { messages?: BatchMessage[] | null }) {
  const [open, setOpen] = useState(false);
  const list = messages ?? [];
  if (list.length === 0) return null;

  const latest = list[list.length - 1];
  const shown = open ? list : [latest];
  return (
    <div className={styles.msgLog}>
      <div className={open ? styles.msgList : styles.msgListCollapsed}>
        {shown.map((m, i) => (
          <div
            key={`${m.time ?? ''}-${i}`}
            className={`${styles.msgItem} ${m.level === 'error' ? styles.msgError : ''
            } ${m.level === 'warn' ? styles.msgWarn : ''}`}
            title={m.text ?? ''}
          >
            <span className={styles.msgTime}>{hhmmss(m.time)}</span>
            <span className={styles.msgText}>{m.text || '-'}</span>
          </div>
        ))}
      </div>
      {list.length > 1 && (
        <Button
          size="small"
          type="link"
          className={styles.msgToggle}
          icon={open ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setOpen(v => !v)}
        >
          {open ? '收起' : `全部 ${list.length} 条`}
        </Button>
      )}
    </div>
  );
}
